import { entityTagToCountry, formatTicker } from '@api';
import { Icon } from '@design/components/icon/Icon';
import { FlagIcon } from '@terminal/components/flag-icon/FlagIcon';
import { NEW_YORK_TIMEZONE } from '@terminal/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { LineData } from '@terminal/modules/market-reaction-charts/types';
import { ThemeLogoIcon } from '@terminal/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { formatInTimeZone } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';

import styles from './CatalystMultipleMarketReactionPrice.module.scss';
import { MarketReactionLegendRow } from './MarketReactionLegendRow';

export interface CatalystMultipleMarketReactionPriceProps {
  lines: LineData[];
  lineColors: string[];
  isWindowComplete: boolean;
  isAfterHoursChart?: boolean;
}

export const CatalystMultipleMarketReactionPrice = ({
  lines,
  lineColors,
  isWindowComplete,
  isAfterHoursChart,
}: CatalystMultipleMarketReactionPriceProps) => {
  const { t } = useTranslation(['insights']);

  const latestDataPointTime = lines.reduce<string | undefined>(
    (latest, line) => {
      const time = line.data[line.data.length - 1]?.time;

      if (!time) {
        return latest;
      }

      return !latest || time > latest ? time : latest;
    },
    undefined
  );

  const referenceDate = latestDataPointTime ?? lines[0]?.prevCloseData?.time;

  const getDateLabel = () => {
    if (!referenceDate) {
      return null;
    }

    if (isWindowComplete) {
      if (isAfterHoursChart) {
        return t('insights:catalyst.drawer.asOfDate', {
          date: `${formatInTimeZone(
            referenceDate,
            NEW_YORK_TIMEZONE,
            'MMM d, h:mm a'
          )} ET`,
        });
      }

      return t('insights:catalyst.drawer.asOfDateClose', {
        date: formatInTimeZone(referenceDate, 'UTC', 'MMM d'),
      });
    }

    return t('insights:catalyst.drawer.asOfDate', {
      date: `${formatInTimeZone(
        referenceDate,
        NEW_YORK_TIMEZONE,
        'MMM d, h:mm a'
      )} ET`,
    });
  };

  const dateLabel = getDateLabel();

  return (
    <>
      {dateLabel && <span className={styles.dateSpan}>{dateLabel}</span>}
      <div className={styles.legendWrapper}>
        {lines.map((line, index) => {
          const { subject } = line;

          if (subject.type === 'theme') {
            return (
              <MarketReactionLegendRow
                key={subject.data.id}
                color={lineColors[index]}
                name={subject.data.name}
                proportionChange={line.lastProportionChange}
                icon={
                  <ThemeLogoIcon
                    name={subject.data.name}
                    logo_url={subject.data.logo_url}
                    size={16}
                  />
                }
              />
            );
          }

          const countryCode = entityTagToCountry[subject.data.tag];
          return (
            <MarketReactionLegendRow
              key={subject.data.tag}
              color={lineColors[index]}
              name={subject.data.name_short}
              ticker={formatTicker(subject.data)}
              proportionChange={line.lastProportionChange}
              icon={
                countryCode ? (
                  <FlagIcon
                    countryCode={countryCode}
                    variant="circular"
                    customSize={16}
                  />
                ) : (
                  <Icon iconName="Globe" size={16} viewBox="4 4 32 32" />
                )
              }
            />
          );
        })}
      </div>
    </>
  );
};
